import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, TrendingUp, Ruler, Camera, Target, Pencil, Trash2, Upload, Loader2, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import PhotoEvolutionPanel from "@/components/photos/PhotoEvolutionPanel";

const TABS = [
  { key: "weight", label: "Poids", icon: TrendingUp },
  { key: "measurements", label: "Mensurations", icon: Ruler },
  { key: "photos", label: "Photos", icon: Camera },
];

const MEASUREMENT_LABELS = {
  chest: { label: "Poitrine", color: "#ec4899", unit: "cm" },
  waist: { label: "Taille", color: "#f59e0b", unit: "cm" },
  arms: { label: "Bras", color: "#8b5cf6", unit: "cm" },
  thighs: { label: "Cuisses", color: "#10b981", unit: "cm" },
};

const emptyForm = { date: new Date().toISOString().split('T')[0], weight: "", notes: "", measurements: { chest: "", waist: "", arms: "", thighs: "" } };

function MiniChart({ data, dataKey, color, label, unit }) {
  if (data.length < 2) return (
    <Card className="border-slate-100"><CardContent className="p-4 text-center py-8"><p className="text-xs text-slate-400">Pas assez de données pour {label}</p></CardContent></Card>
  );
  return (
    <Card className="border-slate-100">
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-slate-700 mb-3">{label} ({unit})</p>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export default function Progress() {
  const [logs, setLogs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("weight");
  const [form, setForm] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [goal, setGoal] = useState(null);
  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoCaption, setPhotoCaption] = useState("");
  const fileInputRef = useRef(null);

  // Photo request banner
  const [photoRequest, setPhotoRequest] = useState(null);

  const fetchLogs = () => { api.get('/progress').then(r => setLogs(r.data)).catch(() => {}); };
  const fetchPhotos = () => { api.get('/photos').then(r => setPhotos(r.data || [])).catch(() => {}); };

  useEffect(() => {
    fetchLogs();
    fetchPhotos();
    api.get('/students/goal').then(r => { if (r.data.goal_weight) setGoal(r.data); }).catch(() => {});
    api.get('/inactivity/my-requests').then(r => { if (r.data.active_request) setPhotoRequest(r.data.active_request); }).catch(() => {});
  }, []);

  const resetForm = () => { setForm({ ...emptyForm, date: new Date().toISOString().split('T')[0] }); setEditing(null); };

  const openEdit = (log) => {
    setEditing(log.log_id);
    setForm({
      date: log.date,
      weight: log.weight || "",
      notes: log.notes || "",
      measurements: {
        chest: log.measurements?.chest || "",
        waist: log.measurements?.waist || "",
        arms: log.measurements?.arms || "",
        thighs: log.measurements?.thighs || "",
      }
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      date: form.date,
      weight: form.weight ? Number(form.weight) : null,
      notes: form.notes,
      measurements: Object.fromEntries(Object.entries(form.measurements).filter(([, v]) => v).map(([k, v]) => [k, Number(v)])),
    };
    try {
      if (editing) {
        await api.put(`/progress/${editing}`, payload);
        toast.success("Progression mise à jour");
      } else {
        await api.post('/progress', payload);
        toast.success("Progression enregistrée");
      }
      setOpen(false);
      resetForm();
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try {
      await api.delete(`/progress/${logId}`);
      toast.success("Entrée supprimée");
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // ─── Photo Upload ───
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5MB)");
      return;
    }

    setPhotoUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post('/photos', {
          image_data: reader.result,
          caption: photoCaption || "",
        });
        toast.success("Photo ajoutée !");
        setPhotoCaption("");
        fetchPhotos();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur d'upload");
      } finally {
        setPhotoUploading(false);
      }
    };
    reader.readAsDataURL(file);
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success("Photo supprimée");
      fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const reversedLogs = [...logs].reverse();
  const chartData = reversedLogs.filter(l => l.weight).map(l => ({ date: l.date, poids: l.weight }));
  const measurementData = (key) => reversedLogs.filter(l => l.measurements?.[key]).map(l => ({ date: l.date, [key]: l.measurements[key] }));

  return (
    <div data-testid="progress-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Progression</h1>
          <p className="text-slate-500 mt-1">Suivez votre évolution</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="add-progress-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle entrée
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Modifier l'entrée" : "Enregistrer ma progression"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" /></div>
              <div><Label>Poids (kg)</Label><Input type="number" step="0.1" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} className="mt-1.5 h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Poitrine (cm)</Label><Input type="number" value={form.measurements.chest} onChange={e => setForm({ ...form, measurements: { ...form.measurements, chest: e.target.value } })} className="mt-1.5 h-11" /></div>
                <div><Label>Taille (cm)</Label><Input type="number" value={form.measurements.waist} onChange={e => setForm({ ...form, measurements: { ...form.measurements, waist: e.target.value } })} className="mt-1.5 h-11" /></div>
                <div><Label>Bras (cm)</Label><Input type="number" value={form.measurements.arms} onChange={e => setForm({ ...form, measurements: { ...form.measurements, arms: e.target.value } })} className="mt-1.5 h-11" /></div>
                <div><Label>Cuisses (cm)</Label><Input type="number" value={form.measurements.thighs} onChange={e => setForm({ ...form, measurements: { ...form.measurements, thighs: e.target.value } })} className="mt-1.5 h-11" /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11">
                {loading ? "Enregistrement..." : editing ? "Mettre à jour" : "Enregistrer"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {goal && (
        <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
          <Target className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <span className="text-sm text-slate-700">Objectif : <strong>{goal.goal_weight} kg</strong></span>
          {goal.goal_date && <span className="text-xs text-slate-400">avant le {new Date(goal.goal_date).toLocaleDateString("fr-FR")}</span>}
        </div>
      )}

      {/* Bannière demande de photos du coach */}
      {photoRequest && (
        <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-purple-900">Votre coach demande vos photos de progression</p>
            {photoRequest.reason && <p className="text-xs text-purple-600 mt-0.5">{photoRequest.reason}</p>}
          </div>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-full gap-1.5 flex-shrink-0"
            onClick={() => { setTab("photos"); setTimeout(() => fileInputRef.current?.click(), 200); }}
          >
            <Upload className="w-3.5 h-3.5" /> Ajouter
          </Button>
        </div>
      )}

      <div className="flex gap-2 mt-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "weight" && (
        <>
          {chartData.length > 1 && (
            <Card className="border-slate-100 mt-6">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Évolution du poids</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" domain={['auto', 'auto']} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }} />
                    <Line type="monotone" dataKey="poids" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          <div className="space-y-3 mt-6">
            {logs.length === 0 ? (
              <div className="text-center py-12"><TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-400">Commencez à tracker votre progression</p></div>
            ) : logs.map(log => (
              <Card key={log.log_id} className="border-slate-100">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{log.date}</p>
                      {log.notes && <p className="text-xs text-slate-500 mt-1">{log.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex gap-1.5 flex-wrap justify-end">
                        {log.weight && <Badge variant="secondary">{log.weight} kg</Badge>}
                        {log.measurements?.chest && <Badge variant="outline" className="text-xs">Poit. {log.measurements.chest}</Badge>}
                        {log.measurements?.waist && <Badge variant="outline" className="text-xs">Taille {log.measurements.waist}</Badge>}
                        {log.measurements?.arms && <Badge variant="outline" className="text-xs">Bras {log.measurements.arms}</Badge>}
                        {log.measurements?.thighs && <Badge variant="outline" className="text-xs">Cuisses {log.measurements.thighs}</Badge>}
                      </div>
                      <div className="flex gap-0.5 ml-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(log)}>
                          <Pencil className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(log.log_id)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {tab === "measurements" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {Object.entries(MEASUREMENT_LABELS).map(([key, cfg]) => (
            <MiniChart key={key} data={measurementData(key)} dataKey={key} color={cfg.color} label={cfg.label} unit={cfg.unit} />
          ))}
        </div>
      )}

      {tab === "photos" && (
        <div className="mt-6 space-y-6">
          {/* Upload section */}
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Input
                    value={photoCaption}
                    onChange={e => setPhotoCaption(e.target.value)}
                    placeholder="Légende (optionnel)..."
                    className="h-10 text-sm"
                  />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  disabled={photoUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full gap-2"
                >
                  {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {photoUploading ? "Upload..." : "Ajouter une photo"}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Formats : JPG, PNG, GIF, WebP. Max 5MB</p>
            </CardContent>
          </Card>

          <PhotoEvolutionPanel
            photos={photos}
            showDelete
            onDelete={handleDeletePhoto}
            emptyMessage="Aucune photo. Utilisez le bouton ci-dessus pour en ajouter."
          />
        </div>
      )}
    </div>
  );
}
